<template>
    <div class="table-block-content grid" 
         :class="[
            hiddenPrintClassName,
            {
            'overflows-left': ElementOverflowsLeft,
            'overflows-right': ElementOverflowsRight
            }
         ]" 
         v-cloak>
        <div class="table-block-content__table-wrap" ref="tableWrapper">
            <div class="table-block-content__table" ref="tableContainer">
                <div class="table-sticky-header table-responsive"
                        :class="{ 'highlight-on-hover': !disableRowHighlighting }"
                        ref="tableEl"
                        :id="`table_${tableId}`">
                    <table class="table b-table table-striped">
                        <thead v-if="useFirstRowAsHeaders || (headers && headers.length > 0)" role="rowgroup">
                            <tr role="row">
                                <th v-for="(field, index) in fields"
                                    :key="field.key"
                                    role="columnheader"
                                    scope="col"
                                    :aria-colindex="index + 1"
                                    :aria-label="field.label"
                                    :aria-sort="field.sortable ? (sortBy === field.key ? sortOrder : 'none') : null"
                                    :class="{ 'table-sticky-column': field.stickyColumn || index === 0 }"
                                    @keypress="field.sortable ? sortItemsOnEnter($event, field.key) : null"
                                    @click="field.sortable ? sortItems(field.key) : null"
                                    :style="field.cssWidth ? { 'max-width': field.cssWidth.replace('width:', '') } : {}"
                                    :tabindex="field.sortable ? '0' : null">
                                    <div><span>{{decodeHtmlEntities(field.label)}}</span></div>
                                </th>
                            </tr>
                        </thead>
                        <tbody role="rowgroup">
                            <tr v-for="(item, rowIndex) in displayItems" :key="rowIndex">
                                <template v-for="(field, colIndex) in fields" :key="`${rowIndex}-${colIndex}`">
                                    <td v-if="item[field.key] !== null || isSortingBy"
                                        :colspan="isSortingBy ? null : (colspan[`${rowIndex}-${colIndex}`] || null)"
                                        :rowspan="isSortingBy ? null : (rowspan[`${rowIndex}-${colIndex}`] || null)"
                                        :class="{ 'table-sticky-column': field.stickyColumn || (stickyColumns.includes(`${rowIndex}-${colIndex}`)) }">
                                        <span v-html="item[field.key] || ''"></span>
                                    </td>
                                </template>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="footnote" class="table-block-content__footnote" v-html="footnote"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

    export default {
        name: 'TableBlock',
        props: {
            rawItems: {
                type: Array,
                default: () => []
            },
            rawFields: {
                type: Array,
                default: () => []
            },
            colspan: {
                type: Object,
                default: () => ({})
            },
            rowspan: {
                type: Object,
                default: () => ({})
            },
            stickyColumns: {
                type: Array,
                default: () => []
            },
            footnote: {
                type: String,
                default: ''
            },
            hiddenPrintClassName: {
                type: String,
                default: ''
            },
            useFirstRowAsHeaders: {
                type: Boolean,
                default: false
            },
            headers: {
                type: Array,
                default: () => []
            },
            disableRowHighlighting: {
                type: Boolean,
                default: false
            }
        },
        setup(props) {

            const tableWrapper = ref(null);
            const tableContainer = ref(null);
            const tableEl = ref(null);

            const tableScrollWidth = ref(0);
            const tableClientWidth = ref(0);
            const tableScrollLeft = ref(0);
            const sortOrder = ref('none');
            const sortBy = ref('');

            const tableId = ref(`table_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`);

            const fields = computed(() => {
                return (props.rawFields || []).filter((f) => {
                    // find in rawItems array only items that contain f.key as string (not null/undefined)
                    return props.rawItems.some(item => {
                        return item[f.key] !== null && item[f.key] !== undefined
                    });
                });
            });
            const displayItems = computed(() => {
                return props.rawItems || [];
            });

            const colspan = computed(() => props.colspan || {});
            const rowspan = computed(() => props.rowspan || {});

            const isSortingBy = computed(() => sortBy.value !== '');

            const ElementOverflowsLeft = computed(() => tableScrollLeft.value !== 0);

            const ElementOverflowsRight = computed(() => {
                return !(tableScrollWidth.value === tableClientWidth.value + tableScrollLeft.value);
            });

            const sortItems = (key) => {
                if (!key) return;

                sortOrder.value = sortBy.value === key
                    ? (sortOrder.value === 'descending' ? 'ascending' : 'descending')
                    : 'ascending';
                sortBy.value = key;

                props.rawItems.sort((a, b) => {
                    const valueA = a[key];
                    const valueB = b[key];

                    if (valueA == null && valueB == null) return 0;
                    if (valueA == null) return 1;
                    if (valueB == null) return -1;

                    if (!isNaN(valueA) && !isNaN(valueB)) {
                        return sortOrder.value === 'ascending' ? valueA - valueB : valueB - valueA;
                    }

                    return sortOrder.value === 'ascending'
                        ? String(valueA).localeCompare(String(valueB))
                        : String(valueB).localeCompare(String(valueA));
                });
            };

            const sortItemsOnEnter = (e, key) => {
                if (e.key === 'Enter') {
                    sortItems(key);
                }
            };

            const updateAutoWidths = () => {
                if (tableContainer.value) {
                    tableClientWidth.value = tableContainer.value.clientWidth;
                    tableScrollWidth.value = tableContainer.value.scrollWidth;
                    tableScrollLeft.value = tableContainer.value.scrollLeft;
                }
            };

            const onTableResize = () => {
                if (!tableEl.value) return;
                if (tableEl.value.clientWidth < tableEl.value.scrollWidth) {
                    tableEl.value.classList.add('has-horizontal-scrollbar');
                } else {
                    tableEl.value.classList.remove('has-horizontal-scrollbar');
                }
            };

            const onTableScroll = () => {
                if (!tableEl.value) return;

                tableScrollLeft.value = tableEl.value.scrollLeft;

                if (tableScrollLeft.value > 0) {
                    tableEl.value.classList.add('scrolling');
                } else {
                    tableEl.value.classList.remove('scrolling');
                }

                const table = tableEl.value.querySelector('table');
                if (table && tableScrollLeft.value + tableEl.value.offsetWidth + 1 >= table.offsetWidth) {
                    tableEl.value.classList.add('scrolled-to-end');
                } else {
                    tableEl.value.classList.remove('scrolled-to-end');
                }
            };

            const decodeHtmlEntities = (html) => {
                if (!html) return '';
                try {
                    const txt = document.createElement('textarea');
                    txt.innerHTML = html;
                    return txt.value;
                } catch (e) {
                    return html;
                }
            };

            onMounted(() => {
                nextTick(() => {
                    if (tableEl.value) {
                        onTableResize();
                        updateAutoWidths();
                        tableEl.value.addEventListener('scroll', onTableScroll);
                    }
                    window.addEventListener('resize', onTableResize);
                });
            });

            onBeforeUnmount(() => {
                window.removeEventListener('resize', onTableResize);
                if (tableEl.value) {
                    tableEl.value.removeEventListener('scroll', onTableScroll);
                }
            });

            return {
                tableWrapper,
                tableContainer,
                tableEl,
                fields,
                displayItems,
                colspan,
                rowspan,
                sortOrder,
                sortBy,
                tableId,
                isSortingBy,
                ElementOverflowsLeft,
                ElementOverflowsRight,
                sortItemsOnEnter,
                sortItems,
                decodeHtmlEntities
            };
        }
    }
</script>